import { type MouseEvent, type PropsWithChildren, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import { CopyIcon } from '@dynatrace/strato-icons';

import { COPY_TO_CLIPBOARD_MESSAGES } from './copy-to-clipboard-messages.js';
import { useCopyToClipboard } from '../../../../core/utils/useCopyToClipboard.js';
import { Tooltip } from '../../../../overlays/tooltip/Tooltip.js';

interface CopyToClipboardProps {
  /* Text to copy to the clipboard */
  textToCopy: string;
  /* Text in tooltip for copy button. Default Copy/Copied. */
  tooltipText?: string[] | string;
}

type CopyToClipboardType = PropsWithChildren<CopyToClipboardProps>;

export const CopyToClipboard = ({
  textToCopy,
  tooltipText,
}: CopyToClipboardType) => {
  const intl = useIntl();
  const defaultTooltipText = [
    intl.formatMessage(COPY_TO_CLIPBOARD_MESSAGES.tooltipActionCopy),
    intl.formatMessage(COPY_TO_CLIPBOARD_MESSAGES.actionCopied),
  ];

  const [copyToClipboard, copied, setCopied] = useCopyToClipboard(textToCopy);

  const handleCopy = (e: MouseEvent) => {
    e.stopPropagation();
    copyToClipboard();
  };

  const getArrayOfTooltipTexts = (values: string | string[]): string[] => {
    if (typeof values === 'string') {
      return [values, values];
    }

    return [values[0], values[1] ?? values[0]];
  };

  const tooltipMessages = useMemo(
    () =>
      tooltipText ? getArrayOfTooltipTexts(tooltipText) : defaultTooltipText,
    [tooltipText],
  );

  const MemoizedButton = useMemo(
    () => (
      <Button
        data-testid="copy-to-clipboard-button"
        onClick={handleCopy}
        variant="emphasized"
      >
        <Button.Prefix>
          <CopyIcon />
        </Button.Prefix>
      </Button>
    ),
    [],
  );

  return useMemo(
    () => (
      <Tooltip
        text={copied ? tooltipMessages[1] : tooltipMessages[0]}
        placement="top"
        delay="none"
        onOpenChange={() => setCopied(false)}
      >
        {MemoizedButton}
      </Tooltip>
    ),
    [tooltipMessages, copied],
  );
};

CopyToClipboard['displayName'] = 'CopyToClipboard';
